import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { Class } from '~/Components/Icons/eventTypes/Class';
import { FormName } from '~/Components/Icons/form/FormName';
import { Clock, Lock, MapIcon, Spots } from '~/Components/Icons';
import { SportCategories } from '~/Components/Icons/events/SportCategories';
import { EventCreateSectionStyles } from '~/Screens/Events/EventCreate/EventCreateSections/hooks';
import {
  ModalSelectLocationPopover,
  ModalSelectTrainerPopover,
} from '~/UIKit/Form/ModalSelect/popovers';
import { ModalSelect } from '~/UIKit/Form/ModalSelect';
import { selectLocationsMap, selectTrainersMap } from '~/store/provider';
import { SwitcherWithLabel } from '~/UIKit/Form/Switcher/SwitcherWithLabel';
import { TextFieldCommon } from '~/UIKit/Form/TextField/TextFieldCommon';
import { KeyboardTimePicker } from '~/UIKit';
import { ModalSelectSportCategoryPopover } from '~/UIKit/Form/ModalSelect/popovers/ModalSelectSportCategoryPopover';
import { selectSportCategoriesMap } from '~/store/events';
import { useSportCatgeroy } from '~/Helpers/SportCategory';
export enum InputType {
  TEXT = 'text',
  NUMBER = 'number',
}

export const useEventCreationFormInputs = () => {
  const { t } = useTranslation();
  const { classes } = EventCreateSectionStyles();
  const locationsMap = useSelector(selectLocationsMap);
  const trainersMap = useSelector(selectTrainersMap);
  const sportCategoriesMap = useSelector(selectSportCategoriesMap);
  const { getCategory } = useSportCatgeroy();
  return useMemo(
    () => ({
      name: {
        icon: <Class className={classes.eventCreateSectionClassIcon} />,
        label: `${t('form.field.title')}*`,
        placeholder: t('form.placeholder.title'),
        name: 'name',
        type: InputType.TEXT,
        noIconBackground: true,
        InputProps: { inputProps: { maxLength: 50 } },
        FormInput: TextFieldCommon,
      },
      trainer: {
        icon: <FormName />,
        label: t('form.field.trainer'),
        placeholder: t('form.placeholder.trainer'),
        name: 'trainerIds',
        formatValue: (selectedTrainerIds: string[]) =>
          selectedTrainerIds?.map((id) => trainersMap?.[id]?.firstName ?? '').join(', '),
        Modal: ModalSelectTrainerPopover,
        FormInput: ModalSelect,
      },
      location: {
        icon: <MapIcon />,
        label: t('form.field.location'),
        placeholder: t('form.placeholder.location'),
        name: 'locationId',
        formatValue: (locationId: string) => locationsMap?.[locationId]?.name,
        Modal: ModalSelectLocationPopover,
        FormInput: ModalSelect,
      },
      sportCategory: {
        icon: <SportCategories />,
        label: t('event.sportCategory'),
        placeholder: t('event.selectCategory'),
        name: 'categoryId',
        formatValue: (id: number) => id && getCategory(id),
        Modal: ModalSelectSportCategoryPopover,
        FormInput: ModalSelect,
      },
      seats: {
        icon: <Spots />,
        label: `${t('form.field.spots')}*`,
        name: 'seats',
        type: InputType.NUMBER,
        InputProps: { inputProps: { min: 0 } },
        FormInput: TextFieldCommon,
      },
      isPublic: {
        icon: <Lock />,
        name: 'public',
        label: t('form.field.public'),
        secondaryLabel: t('form.field.private'),
        FormInput: SwitcherWithLabel,
      },
      startTime: {
        icon: <Clock />,
        name: 'start',
        inputFormat: 'HH:mm',
        label: t('time.startTime'),
        placeholder: t('time.startTime'),
        FormInput: KeyboardTimePicker,
      },
      endTime: {
        icon: <Clock />,
        name: 'end',
        inputFormat: 'HH:mm',
        label: t('time.endTime'),
        placeholder: t('time.endTime'),
        FormInput: KeyboardTimePicker,
      },
    }),
    [t, locationsMap, trainersMap, sportCategoriesMap],
  );
};
